/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Avatar, Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useRequester } from '../../hooks';
import { Page, PassFail } from '../../design';
import { StudentData, StudentResource } from '../../resources';
import StatisticResource, { Statistic } from '../../resources/StatisticResource';
import ChatResource, { ChatData } from '../../resources/ChatResource';
import { formatDate } from '../../utils';
import { ArrowBack } from '@mui/icons-material';
import LineChartWithAPI from '../dashboard/LineChartWithAPI';
import { useEffect, useState } from 'react';
import { Requester } from '../../hooks/useRequester';

function ChatCard({
  chat,
  section,
  compact,
  onClick,
  selected,
}: {
  chat: ChatData;
  section?: string;
  compact?: boolean;
  onClick?: () => void;
  selected?: boolean;
}) {
  const type = section || chat.meta?.eikenTestType;

  const { title, passScore, maxScore, score } = ChatResource.getScoreForType(chat, type);

  const timestamp = formatDate(chat.createdAt);

  let passfailType;

  if (Number(score) && passScore) {
    passfailType = Number(score) >= passScore ? 'pass' : 'fail';
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        p: compact ? 2 : 3,
        flexDirection: compact ? 'column' : 'row',
        justifyContent: compact ? 'flex-start' : 'space-between',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
        outline: selected ? '2px solid' : 'none',
        outlineColor: 'primary.2',
        backgroundColor: selected ? 'background.primary' : 'none',
      }}
      className="flex justify-between"
      onClick={onClick}
    >
      <Box className="flex flex-col">
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {timestamp}
        </Typography>
      </Box>

      <Box className="flex flex-row justify-between items-center gap-4">
        <Typography variant="h3">
          {score || '-'}/{maxScore}
        </Typography>
        <PassFail type={passfailType} />
      </Box>
    </Card>
  );
}

async function fetchLatestChats(api: Requester, userId: string) {
  await api.submit({ userId });
  await api.submit({ userId, type: 'fulltest' });
  await api.submit({ userId, type: 'pronunciation' });
  await api.submit({ userId, type: 'q1' });
  await api.submit({ userId, type: 'q2' });
  await api.submit({ userId, type: 'q3' });
  await api.submit({ userId, type: 'q4' });
}

export default function StudentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [testType, setTestType] = useState<string>('*');

  const studyTimeApi = useRequester<StatisticResource[]>({
    onSubmit: (q) => StatisticResource.fetchStatistic(Statistic.EikenStudyTimes, q),
    onError: console.log,
  });

  const scoreApi = useRequester<StatisticResource[]>({
    onSubmit: (q) => StatisticResource.fetchStatistic(Statistic.EikenScores, q),
    onError: console.log,
  });

  const latestChatsApi = useRequester<ChatResource[]>({
    onSubmit: ({ userId, type }) => ChatResource.fetchLatestChatsForUser(userId, type),
    onError: console.log,
  });

  useEffect(() => {
    if (id) fetchLatestChats(latestChatsApi, id);
  }, [id]);

  const fetchStudent = useRequester<StudentResource>({
    onSubmit: (id) => StudentResource.fetchOne(id),
    onError: console.log,
  });

  useEffect(() => {
    fetchStudent.submit(id);
  }, [id]);

  if (!id) return <Alert severity="error">No student ID provided</Alert>;
  const student = StudentResource.get(id).data as StudentData;
  if (!student) {
    return (
      <Box className="flex flex-col items-center h-full justify-center">
        {fetchStudent.error ? (
          <Alert severity="error">{fetchStudent.error}</Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full">
          <Button
            className="flex flex-row items-center gap-2 max-w-fit"
            onClick={() => navigate('/students')}
            sx={{ mt: 1 }}
          >
            <ArrowBack fontSize="small" /> Back
          </Button>

          <Box className="flex flex-row items-center gap-4" sx={{ mt: 3 }}>
            <Box className="flex flex-col items-center gap-2">
              <Avatar src={student.avatar} sx={{ width: 80, height: 80 }} />
            </Box>
            <Box className="flex flex-col h-full">
              <Typography variant="body1" color="text.secondary">
                Students / Student Profile
              </Typography>
              <Typography variant="h1">
                {student.firstName} {student.lastName}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box sx={{ mt: 5 }} className="grid grid-cols-2 gap-6">
          <LineChartWithAPI
            userId={id}
            configs={[
              {
                title: 'Study Time',
                statistic: Statistic.EikenStudyTimes,
                toDatapoint: (item) => ({
                  x: StatisticResource.parseId(item.data.id).startDate.toDate().getTime(),
                  y: Math.round(item.data.sum / 60),
                }),
                toAverage: (values) => {
                  const avg =
                    values.reduce((acc, item) => acc + item.data.sum, 0) / values.length / 60;

                  return avg.toFixed(0) + ' min';
                },
                fetcher: studyTimeApi,
                margin: { left: 28, right: 52, top: 16, bottom: 30 },
              },
            ]}
          />
        </Box>

        <Box className="flex flex-col gap-6" sx={{ mt: 5 }}>
          <Typography variant="h2">Questions</Typography>
          <Box className="grid grid-cols-6 gap-6">
            {student.latestChat &&
              ['fulltest', 'pronunciation', 'q1', 'q2', 'q3', 'q4'].map((section) => (
                <ChatCard
                  key={section}
                  chat={
                    student.latestChat?.meta?.eikenTestType === 'fulltest'
                      ? student.latestChat
                      : ChatResource.listLatestChatsForUser(id, section)[0]?.data ||
                        student.latestChat
                  }
                  section={section}
                  compact
                  onClick={() => setTestType(testType === section ? '*' : section)}
                  selected={testType === section}
                />
              ))}
          </Box>
        </Box>

        <Box sx={{ mt: 5 }} className="grid grid-cols-2 gap-6">
          <LineChartWithAPI
            userId={id}
            eikenTestType={testType}
            configs={[
              {
                title: 'Scores',
                statistic: Statistic.EikenScores,
                toDatapoint: (item) => ({
                  x: StatisticResource.parseId(item.data.id).startDate.toDate().getTime(),
                  y: item.data.avg,
                }),
                toAverage: (values) => {
                  const validValues = values.filter((item) => item.data.count > 0);
                  if (validValues.length === 0) return 'N/A';
                  const avg =
                    validValues.reduce((acc, item) => acc + item.data.avg, 0) / validValues.length;
                  return avg.toFixed(2);
                },
                fetcher: scoreApi,
                margin: { left: 28, right: 52, top: 16, bottom: 30 },
              },
            ]}
          />

          <Box>
            <Box className="flex flex-row justify-between items-center">
              <Typography variant="h2">Latest Practices</Typography>
              <Button
                variant="text"
                color="primary"
                onClick={() => navigate(`/chats?userId=${id}`)}
              >
                View all
              </Button>
            </Box>
            <Box className="flex flex-col gap-3 mt-4">
              {ChatResource.listLatestChatsForUser(id).length > 0 ? (
                ChatResource.listLatestChatsForUser(id).map((chat) => (
                  <ChatCard
                    chat={chat.data}
                    key={chat.data.id}
                    onClick={() => navigate(`/chats/${chat.data.id}`)}
                  />
                ))
              ) : latestChatsApi.loading ? (
                <CircularProgress size={20} />
              ) : (
                <Alert severity="info">No practice found for this student.</Alert>
              )}
            </Box>
          </Box>
        </Box>
      </Page.Content>
    </Page>
  );
}
